/**
 * API errors drill-down modal, opened by clicking a row on the Telemetry
 * page's "API errors" report. Shows every individual request behind one
 * status+route pair over the same time range as the parent report.
 */

import { Loader2 } from 'lucide-react';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { Modal } from '@/components/Modal';
import { formatDateTime } from '@/lib/dateUtils';
import { SOURCE_LABELS, useTelemetryDetail } from '@/lib/useTelemetry';

const COLUMNS = [
  'Date/time',
  'Method',
  'Path',
  'Status',
  'Latency (ms)',
  'Name',
  'Email',
  'Source',
  'IP',
  'User agent',
];

export function ApiErrorDetailModal({
  status,
  route,
  hours,
  onClose,
}: {
  status: string;
  route: string;
  hours: number;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useTelemetryDetail(
    'api_error_detail',
    { status, route, hours },
    true,
  );
  const rows = data?.rows ?? [];

  return (
    <Modal title={`${status} - ${route}`} eyebrow="API errors" onClose={onClose} widthClassName="max-w-4xl">
      <div className="max-h-[70vh] overflow-auto p-5">
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              {COLUMNS.map((label) => (
                <th key={label} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading requests...
                  </span>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-red-700 dark:text-red-300">
                  Could not load requests: {error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No requests in this window.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className="border-t border-amp-line dark:border-amp-dark-line">
                  <td className="px-3 py-2 whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                    {formatDateTime(r.ts)}
                  </td>
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{r.httpMethod || '—'}</td>
                  <td className="px-3 py-2 break-all text-amp-ink dark:text-amp-dark-ink">{r.path || '—'}</td>
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{r.status || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">
                    {r.responseLatencyMs || '—'}
                  </td>
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{r.name || '—'}</td>
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{r.email || '—'}</td>
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                    {SOURCE_LABELS[r.source ?? ''] ?? r.source ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{r.sourceIp || '—'}</td>
                  <td className="px-3 py-2 break-all text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    {r.userAgent || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
